import { DialogTitle } from '@/components/ui/dialog'
import { useHall } from '@/contexts/HallProvider'
import { HallUser, Winner } from '@db8/shared'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { Button } from '@/components/ui/button'
import { ArrowBigLeft } from 'lucide-react'
import { usePoll } from '@/contexts/PollProvider'
import ProfileImage from '@/components/ProfileImage'

const BallotCard = ({ ballot, teamMembers }: { ballot: any; teamMembers: HallUser[] }) => {
  const decisionColor =
    ballot.decision === 'AFFIRMATIVE'
      ? 'text-green-600'
      : ballot.decision === 'NEGATIVE'
        ? 'text-blue-600'
        : 'text-gray-600'

  const feedbackRows = ballot.feedbacks.sort((a, b) => a.rank - b.rank)

  return (
    <div className="flex flex-col space-y-2 rounded-md border p-4 shadow-sm mb-6 bg-background">
      <div className="flex justify-between items-center ">
        <h2 className="text-md font-semibold">
          Judge: {ballot.hallUser?.displayName || 'Anonymous'}
        </h2>
        <span className={`font-semibold ${decisionColor}`}>{ballot.decision} Wins</span>
      </div>
      <p className="text-sm">
        <strong>Low-Point Win:</strong> {ballot.lowPointWin ? 'Yes' : 'No'}
      </p>
      <p className="text-sm">
        <strong>Reason:</strong> {ballot.reason}
      </p>
      <p className="text-sm">
        <strong>Comments:</strong> {ballot.comments}
      </p>

      {/* <Separator className="my-4" /> */}

      <div className="space-y-2">
        {feedbackRows.map((fb, idx) => {
          const user: any = teamMembers.find((u) => u.id === fb.hallUserId) || {
            name: 'Unknown',
            id: fb.hallUserId,
            imageUrl: '',
          }

          return (
            <div
              key={fb.hallUserId}
              className="p-3 rounded-md bg-muted border text-sm flex flex-col"
            >
              <div className="flex items-center gap-3">
                <ProfileImage
                  user={user}
                  className="max-w-8 w-8 h-8 rounded-full border object-cover"
                />

                <div className="flex justify-between w-full items-center">
                  <span className="font-medium">{user.displayName}</span>
                  <span className="text-xs font-semibold">
                    Points: {fb.points} | Rank: #{fb.rank}
                  </span>
                </div>
              </div>
              {fb.content && <p className="mt-2 italic">"{fb.content}"</p>}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export const BallotResults = ({ onBack }) => {
  const { isModerator } = useHallUsers()
  const { ballots, hallMetadata } = useHall()
  const { hasEveryoneVoted, determineWinner } = usePoll()
  const { hallUsers } = useHallUsers()

  return (
    <div className="flex flex-col h-full space-y-4">
      <div className="shrink-0">
        <DialogTitle className="text-lg font-bold mb-4">
          <Button
            variant="secondary"
            size="sm"
            className="mr-6 dark:bg-background"
            onClick={onBack}
          >
            <ArrowBigLeft size="20px" />
          </Button>
          Ballot Results
        </DialogTitle>
      </div>

      <div className="flex-1 overflow-y-auto pr-1 space-y-4">
        {ballots?.length === 0 ? (
          <p className="h-full w-full flex justify-center items-center text-base font-semibold text-center">
            No ballots have been submitted yet.
          </p>
        ) : (
          ballots.map((ballot) => (
            <BallotCard key={ballot.id} ballot={ballot} teamMembers={hallUsers} />
          ))
        )}
      </div>

      {isModerator && hasEveryoneVoted && hallMetadata?.winningTeam === Winner.Undecided && (
        <div className="shrink-0 pt-2">
          <Button variant="secondary" className="w-full" onClick={determineWinner}>
            Declare Winner
          </Button>
        </div>
      )}
    </div>
  )
}
